/**
 * Custom Theme Provider for PACS Application
 * Supports light/dark mode with auto-switching for imaging pages
 */

import React, { useMemo } from 'react';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { lightPalette, darkPalette } from './palette';
import typography from './typography';
import { lightShadows, darkShadows } from './shadows';
import { getComponentOverrides } from './components';
import { useThemeMode, ThemeContextProvider } from '../context/ThemeContext';

// Create theme based on mode
const createAppTheme = (mode) => {
  const palette = mode === 'dark' ? darkPalette : lightPalette;
  const shadows = mode === 'dark' ? darkShadows : lightShadows;

  return createTheme({
    palette,
    typography,
    shadows,
    shape: {
      borderRadius: 8,
    },
    spacing: 4, // Base spacing unit (4px)
    transitions: {
      easing: {
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
        easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
        sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
      },
      duration: {
        shortest: 150,
        shorter: 200,
        short: 250,
        standard: 300,
        complex: 375,
        enteringScreen: 225,
        leavingScreen: 195,
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 640,
        md: 768,
        lg: 1024,
        xl: 1280,
        xxl: 1536,
      },
    },
    components: getComponentOverrides(palette, shadows),
  });
};

// Inner component that uses theme context
const ThemedApp = ({ children }) => {
  const { mode } = useThemeMode();

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

// Main custom theme component (wraps with context)
export default function CustomTheme({ children }) {
  return (
    <StyledEngineProvider injectFirst>
      <ThemeContextProvider>
        <ThemedApp>{children}</ThemedApp>
      </ThemeContextProvider>
    </StyledEngineProvider>
  );
}

// Re-export for convenience
export { useThemeMode } from '../context/ThemeContext';
export { lightPalette, darkPalette } from './palette';
